import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const AreaDialog = ({ areas, area, open, onClose, handleDeleteArea, handleAddArea }) => {
  const { t } = useTranslation();
  const [newAreaName, setNewAreaName] = useState(area?.areaName);
  const [newAreaError, setNewAreaError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setNewAreaName(area?.areaName || ""); // Ensure it's never undefined
    setNewAreaError(false);  
  }, [area]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle
        sx={{
          fontWeight: "bold",
          textAlign: "center",
          fontSize: { xs: "1.2rem", sm: "1.5rem" },
        }}
      >
        {area?.areaId ? t("admin.areas.editArea") : t("admin.areas.addNewArea")}
      </DialogTitle>

      <DialogContent sx={{ padding: { xs: "16px", sm: "20px 24px" } }}>
        <TextField
          label={t("admin.areas.areaName") + " *"}
          fullWidth
          value={newAreaName}
          onChange={(e) => setNewAreaName(e.target.value)}
          margin="dense"
          variant="outlined"
          error={newAreaError}
          helperText={newAreaError ? t("admin.areas.areaNameError") : ""}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
            },
          }}
        />
      </DialogContent>

      {loading ? (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <DialogActions
          sx={{
            justifyContent: { xs: "center", sm: "space-between" },
            padding: { xs: "12px", sm: "16px 24px" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            color="secondary"
            sx={{ borderRadius: "8px", width: { xs: "100%", sm: "auto" } }}
          >
            {t("admin.areas.cancel")}
          </Button>

          {area?.areaId && (
            <Button
              onClick={async () => {
                setLoading(true);
                await handleDeleteArea(area?.areaId);
                setLoading(false);
              }}
              color="error"
              sx={{
                color: "red",
                "&:hover": { backgroundColor: "red", color: "#fff" },
              }}
            >
              {t("admin.areas.delete")}
            </Button>
          )}

          <Button
            onClick={async () => {
              if (!newAreaName) {
                setNewAreaError(true);
                return;
              }
              setLoading(true);
              await handleAddArea(newAreaName);
              setLoading(false);
            }}
            color="primary"
            sx={{
              borderRadius: "8px",
              width: { xs: "100%", sm: "auto" },
              "&:hover": { backgroundColor: "blue", color: "#fff" },
            }}
          >
            {area?.areaId ? t("admin.areas.update") : t("admin.areas.save")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

AreaDialog.propTypes = {
  areas: PropTypes.array.isRequired,
  area: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleAddArea: PropTypes.func.isRequired,
  handleDeleteArea: PropTypes.func.isRequired,
};

export default AreaDialog;
